'use client';

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Viewport } from './server';

/**
 * Mobile is "≤767px wide" OR "a short screen you touch".
 *
 * The width test alone judged a phone by its longest side the moment it was
 * turned sideways: an iPhone in landscape is 844px across, sails past 767, and
 * the whole app swapped to the desktop shell — sidebar, topbar and all — on top
 * of whatever the engineer was doing. It then wrote `desktop` into the cookie,
 * so the next SSR paint came back desktop too.
 *
 * The second clause closes it. A phone on its side is short (≤500px) and
 * coarse-pointered; both have to be true, so neither a narrow desktop window
 * (fine pointer) nor an iPad in landscape (768px tall, and deliberately treated
 * as desktop) gets caught by it.
 */
export const MOBILE_MQ = '(max-width: 767px), (max-height: 500px) and (pointer: coarse)';
const VIEWPORT_COOKIE = 'mucka_viewport';

interface ViewportContextValue {
  isMobile: boolean;
}

const ViewportContext = createContext<ViewportContextValue | null>(null);

interface ViewportProviderProps {
  initial: Viewport;
  children: ReactNode;
}

/**
 * Seeds the first React render with the SSR-resolved viewport, then
 * keeps the value in sync with the real `MOBILE_MQ` media query
 * once we're on the client. Writes the resolved value back into the
 * mucka_viewport cookie so the next SSR request paints the right
 * layout from byte zero.
 */
export function ViewportProvider({ initial, children }: ViewportProviderProps) {
  const [isMobile, setIsMobile] = useState(initial === 'mobile');

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);

    const apply = (matches: boolean) => {
      setIsMobile(matches);
      // Persist for the next SSR. Lax + 1y mirrors the other
      // mucka_* cookies. Not httpOnly because future client checks
      // could read it too if needed.
      const next = matches ? 'mobile' : 'desktop';
      document.cookie = `${VIEWPORT_COOKIE}=${next}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    };

    apply(mq.matches);
    const handler = (e: MediaQueryListEvent) => apply(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <ViewportContext.Provider value={{ isMobile }}>
      {children}
    </ViewportContext.Provider>
  );
}

/**
 * Returns `true` when the current viewport is mobile (see `MOBILE_MQ`). Reads
 * the SSR-seeded value for the first render and updates in response
 * to matchMedia changes. Drops the matchMedia useState/useEffect
 * boilerplate that used to live in every *Shell component — and,
 * crucially, no longer flashes desktop-first on mobile.
 */
export function useIsMobile(): boolean {
  const ctx = useContext(ViewportContext);
  if (!ctx) {
    // Defensive: a shell rendered outside the (authed) layout (e.g. a
    // public page or storybook) gets an honest matchMedia read with
    // a server-safe `false` default.
    return false;
  }
  return ctx.isMobile;
}

/**
 * The width at which a modal can show its document alongside the form.
 *
 * Not a "desktop" test — `useIsMobile` already owns that. This is the much
 * narrower question of whether there is room for a ~520px form column AND a
 * document page wide enough to read (a quote's items table needs ~640px before
 * the Total column starts fighting the description). Below it, the same modals
 * fall back to the single column they have always been.
 */
export const DESK_MQ = '(min-width: 1240px)';

/**
 * Live `matchMedia` result for an arbitrary query.
 *
 * Seeded synchronously from `window.matchMedia` rather than defaulting to
 * false, so a modal that opens after hydration paints its final layout on the
 * first frame instead of flashing the narrow one. `false` on the server, which
 * is safe: nothing that uses this renders during SSR.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    setMatches(mq.matches);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return matches;
}
